import { useCallback, useEffect, useState } from 'react';
import { getMobileOperatingSystem } from './functions.js';
import { PERMISSION_INITIAL_STATE, PERMISSION_STATUS } from "./constants.js";

export const useDeviceOrientation = () => {
  const [status, setStatus] = useState(PERMISSION_INITIAL_STATE);
  const [orientation, setOrientation] = useState(0);
  const mobileOS = getMobileOperatingSystem();

  const startOrientationCallback = () => {
    chooseSupportedOrientationAPIByOS();
  };

  const chooseSupportedOrientationAPIByOS = async () => {
    try{
      setStatus({status: PERMISSION_STATUS.LOADING, message: ""});
      if (mobileOS === "iOS") {
        if(await checkDeviceOrientationEvent()){
          initDeviceOrientationListener(); 
        }
      }else if(mobileOS === "Android"){
        if(await checkAbsoluteOrientationPermissions()){
          initAbsoluteOrientationSensor();
        };
      }else{
        throw new Error('Not iOS or Android, AR experience not supported');
      }
    }catch(e) {
      setStatus({status: PERMISSION_STATUS.ERROR, message: e.message});
    }
  }

  const checkDeviceOrientationEvent = async () => {
    if (!DeviceOrientationEvent) {
      throw new Error('Device orientation event is not supported by your browser');
    }

    if (
      DeviceOrientationEvent.requestPermission
      && typeof DeviceMotionEvent.requestPermission === 'function'
    ) {
      let permission = await DeviceOrientationEvent.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Request to access the device orientation was rejected');
      }
    }
    return true;
  }

  const getWebkitCompassHeading = (e) => setOrientation(e.webkitCompassHeading);
  
  const calculateRelativeCompassHeading = (e) => {
    const { alpha, beta, gamma} = e;

    // Convertors
    const degToRad = Math.PI / 180;
    const radToDeg = 180 / Math.PI;

    const _x = beta  ? beta  * degToRad : 0; // beta value
    const _y = gamma ? gamma * degToRad : 0; // gamma value
    const _z = alpha ? alpha * degToRad : 0; // alpha value

    const cX = Math.cos( _x );
    const cY = Math.cos( _y );
    const cZ = Math.cos( _z );
    const sX = Math.sin( _x );
    const sY = Math.sin( _y );
    const sZ = Math.sin( _z );

    // Calculate Vx and Vy components
    const Vx = - cZ * sY - sZ * sX * cY;
    const Vy = - sZ * sY + cZ * sX * cY;

    // Calculate compass heading
    let compassHeading = Math.atan( Vx / Vy );

    // Convert compass heading to use whole unit circle
    if( Vy < 0 ) {
      compassHeading += Math.PI;
    } else if( Vx < 0 ) {
      compassHeading += 2 * Math.PI;
    }

    compassHeading = compassHeading * radToDeg;

    setOrientation(compassHeading);
  }
  const initDeviceOrientationListener = () => {
    window.addEventListener("deviceorientation", getWebkitCompassHeading, true);
    setStatus({status: PERMISSION_STATUS.CORRECT, message: ""});
  }

  const checkAbsoluteOrientationPermissions = async () => {
    if (navigator.permissions) {
      try{
        const response = await Promise.all([
          navigator.permissions.query({ name: "accelerometer" }),
          navigator.permissions.query({ name: "magnetometer" }),
          navigator.permissions.query({ name: "gyroscope" })
        ])
        if (response.every(result => result.state === "granted")) {
          return true;
        } else {
          console.log("Permission to use sensor was denied.");
          return false;
        }
      }catch(err){
        console.log("Integration with Permissions API is not enabled, still try to start app.");
        return true;
      }
    } else {
      console.log("No Permissions API, still try to start app.");
      return true;
    }
  }
  const initAbsoluteOrientationSensor = () => {
    
    const options = { frequency: 6, referenceFrame: 'device' };
    const sensor = new window.AbsoluteOrientationSensor(options);
    sensor.addEventListener('reading', () => {
      // model is a Three.js object instantiated elsewhere.
      let q = sensor.quaternion;
      let heading = Math.atan2(2*q[0]*q[1] + 2*q[2]*q[3], 1 - 2*q[1]*q[1] - 2*q[2]*q[2])*(180/Math.PI);
      heading = Math.abs( heading - 360);
      if( heading > 360){
        heading = heading - 360
      }
      setOrientation(heading);
    });

    sensor.addEventListener('error', error => {
      if (error.name === 'NotReadableError') {
        console.log("Sensor is not available.");
      }
      throw new Error(error.name);
    });

    sensor.start();
    setStatus({status: PERMISSION_STATUS.CORRECT, message: ""});
  }

  const revokeAccessAsync = () => {
    window.removeEventListener('deviceorientation', getWebkitCompassHeading, true);
  };

  const startOrientation = useCallback(startOrientationCallback, []);
  const revokeAccess = useCallback(revokeAccessAsync, []);

  useEffect(() => {
    return () => {
      revokeAccess();
    };
  }, [revokeAccess]);

  return {
    orientation,
    status,
    startOrientation,
    revokeAccess,
  };
};